import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateFormCompletionName,
} from '@assets/i18n/config';
import {
  FileDocumentObjectTypes,
  FileDocumentTypes,
} from '@interfaces/fileDocument';
import { Download, Upload } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { PdfViewer } from '@ovComponents/2-component/pdfViewer/pdfViewer';
import { useFileUpload } from '@providers/fileUploadHook';
import { useGlobalToast } from '@providers/globalToastProvider';
import { UserContext } from '@providers/userContextProvider';
import { safeWindowOpen } from '@utils/urlSanitization';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FETCH_FORM_COMPLETIONS } from './manageFormCompletions.queries';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      resolvedPdfFileName { ${TRANSLATED_STRING_FIELDS} }
      templateType
      unsignedUrl
      state
      unsignedDocument {
        id
        downloadUrl
      }
    }
  }
`;

const ARCHIVE_FORM_COMPLETION = gql`
  mutation archiveFormCompletion($input: ArchiveFormCompletionInput!) {
    archiveFormCompletion(input: $input) {
      formCompletion {
        id
        state
      }
    }
  }
`;

const DELETE_FILE_DOCUMENT = gql`
  mutation deleteFileDocument($input: DeleteFileDocumentInput!) {
    deleteFileDocument(input: $input) {
      fileDocument {
        id
      }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId,
  onClose,
  onArchive,
  canArchive,
}: {
  formCompletionId: string;
  onClose: () => void;
  onArchive?: () => void;
  canArchive: boolean;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { showToast } = useGlobalToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const { uploadFile } = useFileUpload();
  const { activeOrganization } = useContext(UserContext);

  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const maxRetries = 3;

  const { data, loading, refetch } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
  });

  // Handle PDF load error with retry
  const handlePdfError = async () => {
    if (pdfRetryCount < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await refetch();
      setPdfRetryCount((prev) => prev + 1);
    } else {
      showToast({
        severity: 'error',
        message: t('workflowCompletions:formPdfLoadError'),
      });
    }
  };

  // Reset retry count when dialog opens with new form
  useEffect(() => {
    setPdfRetryCount(0);
  }, [formCompletionId]);

  const [archiveFormCompletion] = useMutation(ARCHIVE_FORM_COMPLETION);
  const [deleteFileDocument] = useMutation(DELETE_FILE_DOCUMENT);

  const reuploadFile = async (file: File) => {
    try {
      await uploadFile(file, {
        objectType: FileDocumentObjectTypes.FORM_COMPLETION_UNSIGNED,
        objectId: data.fetchFormCompletion.id,
        sourceId: data.fetchFormCompletion.id,
        fileName: file.name,
        mediaType: file.type,
        sourceType: 'FORM_COMPLETION',
        type: FileDocumentTypes.TEMPLATE,
        permissionType: 'PUBLIC',
        name: file.name,
        organizationId: activeOrganization?.id ?? '',
      });
      if (data.fetchFormCompletion.unsignedDocument?.id) {
        await deleteFileDocument({
          variables: {
            input: {
              fileDocumentId: data.fetchFormCompletion.unsignedDocument.id,
            },
          },
        });
      }
      await refetch();
      showToast({
        severity: 'success',
        message: t('workflowCompletions:formReuploaded'),
      });
    } catch {
      showToast({
        severity: 'error',
        message: t('workflowCompletions:formReuploadError'),
      });
    }
  };

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await archiveFormCompletion({
        variables: {
          input: {
            formCompletionId,
          },
        },
        refetchQueries: [FETCH_FORM_COMPLETIONS],
      });
      showToast({
        severity: 'success',
        message: t('workflowCompletions:formArchived'),
      });
      onArchive?.();
      onClose();
    } catch {
      showToast({
        severity: 'error',
        message: t('workflowCompletions:formArchiveError'),
      });
    } finally {
      setIsArchiving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
        <DialogContent>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formCompletion = data?.fetchFormCompletion;

  if (!formCompletion) {
    return null;
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>
        {translateFormCompletionName(formCompletion)}
      </DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2}>
          {formCompletion.templateType === 'PDF' &&
            (formCompletion.unsignedDocument?.downloadUrl ||
              formCompletion.unsignedUrl) && (
              <Box width='100%'>
                <PdfViewer
                  pdfUrl={
                    formCompletion.unsignedDocument?.downloadUrl ||
                    formCompletion.unsignedUrl
                  }
                  fullWidth
                  onError={handlePdfError}
                />
              </Box>
            )}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box display='flex' justifyContent='space-between' width='100%'>
          {formCompletion.state !== 'ARCHIVED' && canArchive && (
            <Button
              label={t('shared:archive')}
              variant='tonal'
              color='destructive'
              onClick={handleArchive}
              disabled={isArchiving}
            />
          )}
          <Box display='flex' gap={2}>
            <Button
              label={t('shared:download')}
              variant='tonal'
              leadingIcon={Download}
              onClick={() =>
                safeWindowOpen(
                  formCompletion.unsignedDocument?.downloadUrl ||
                    formCompletion.unsignedUrl,
                  {
                    target: '_blank',
                    onError: (errorMsg) => {
                      showToast({
                        severity: 'error',
                        message: errorMsg,
                      });
                    },
                  },
                )
              }
            />
            <Button
              label={t('shared:reupload')}
              leadingIcon={Upload}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf';
                input.onchange = (e) => {
                  const f = (e.target as HTMLInputElement).files?.[0];
                  if (f) {
                    reuploadFile(f);
                  }
                };
                input.click();
              }}
            />
          </Box>
        </Box>
      </DialogFooter>
    </Dialog>
  );
};
